<template>
  <div v-if="canUpdate">
    <v-dialog
      v-if="device"
      v-model="showRebootDevice"
      max-width="480px"
    >
      <videon-card :heading="'Reboot ' + device.deviceName()" showClose="true" @close="showRebootDevice = false">
        <v-card-text>
          <p class="subtitle-1 my-5">
            Are you sure you want to reboot <span class="primary--text">{{ device.deviceName() }}</span>?
          </p>
          <v-form @submit.prevent="rebootDevice">
            <v-text-field
              v-model="rebootDeviceConfirm"
              label="Reboot Confirmation"
              messages="Please type CONFIRM above to proceed"
              required
              outlined
            ></v-text-field>
            <div class="mt-3 d-flex align-end">
              <v-spacer />
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showRebootDevice = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="rebootDevice"
              >
                Reboot
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <div v-if="!hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ec-none" />
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-progress-linear
            class="mt-5 mb-2"
            color="white"
            :value="processPercent"
            buffer-value="0"
            v-if="showDeterminateDownloadProgress"
          ></v-progress-linear>
          <v-progress-linear
            class="mt-5 mb-2"
            color="white"
            indeterminate
            buffer-value="0"
            v-else-if="isUpdating"
          ></v-progress-linear>

          <v-btn v-if="applyInProgress" color="secondary" width="100%" class="mt-4" disabled>Updating...</v-btn>
          <v-btn v-else-if="awaitingReboot" @click="showRebootDevice = true" color="red" width="100%" class="mt-4" :disabled="disableUpdateButton">{{ rebootButtonLabel }}</v-btn>
          <v-btn v-else @click="updateAction('check_for_update')" color="secondary" width="100%" class="mt-4" :disabled="disableUpdateButton">Check for Update</v-btn>
        </v-card-text>
      </v-card>
    </div>
    <div v-if="hasUpdates">
      <v-card
        color="info"
        outlined
        dark
        class="my-3"
      >
        <v-card-text>
          <h4 class="text-uppercase white--text d-inline-flex align-center"><v-icon color="white" class="mr-2">system_update_alt</v-icon> Firmware Update Available</h4>
          
          <device-update-state-rows :rows="updateStateDetailRows" row-key-prefix="ec-yes" />
          
          <v-alert
            text
            outlined
            color="black"
            class="mt-3 mb-0"
            v-if="updateStatus === 'rejected' || updateStatus === 'error'"
          >
            {{ updateMessage }}. This may be due to slow download speeds. Check back later to apply update.
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="updateStatus === 'complete'"
          >
            {{ updateMessage }}
          </v-alert>
          
          <v-alert
            text
            outlined
            color="disabled"
            icon="mdi-alert-outline"
            class="mt-3 mb-0"
            v-else-if="device && !device.isOnline() && !isUpdating"
          >
            {{ offlineUpdatesSummary }}. However, you are unable to apply updates while the device is offline.
          </v-alert>
          
          <p class="mb-0 mt-2">
            <a href="https://support.videonlabs.com/hc/en-us/articles/360016605353-Device-Software-and-Release-Notes" target="_blank">Device Software and Release Notes</a>
          </p>
          
          <div v-if="device && device.isOnline()">
            <v-progress-linear
              class="mt-5 mb-2"
              color="white"
              :value="processPercent"
              buffer-value="0"
              v-if="showDeterminateDownloadProgress"
            ></v-progress-linear>
            <v-progress-linear
              class="mt-5 mb-2"
              color="white"
              indeterminate
              buffer-value="0"
              v-else-if="isUpdating"
            ></v-progress-linear>
            <v-btn v-if="applyInProgress" color="secondary" width="100%" class="mt-4" disabled>Updating...</v-btn>
            <v-btn v-else-if="awaitingReboot" @click="showRebootDevice = true" color="red" width="100%" class="mt-4" :disabled="disableUpdateButton">{{ rebootButtonLabel }}</v-btn>
            <v-btn v-else @click="updateAction()" color="secondary" width="100%" class="mt-4" :disabled="disableUpdateButton">{{ primaryUpdateButtonLabel }}</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
  import DeviceUpdateStateRows from './DeviceUpdateStateRows.vue'
  import { runDeviceFirmwareCommand } from './deviceFirmwareCommand.js'
  import firmwareUpdateContract from './firmwareUpdateContract.js'
  
  export default {
    name: 'DeviceFirmwareUpdaterEdgecaster',
    components: {
      DeviceUpdateStateRows,
    },
    props: ['device'],
    
    data() {
      return {
        showRebootDevice: false,
        rebootDeviceConfirm: '',
      }
    },
    
    computed: {
      canUpdate() {
        if (this.device.state && this.device.state.update_state) {
          return true
        }
        return false
      },
      
      canEdit() {
        return this.device.canEdit()
      },
      
      // Prefer daemon over system when both have updates (single firmware card).
      pendingFirmwareUpdate() {
        var available = this.device.updatesAvailable || {}
        return available['daemon'] || available['system'] || available['firmware']
      },
      
      version() {
        return this.pendingFirmwareUpdate
      },
      
      hasUpdates() {
        return !!this.pendingFirmwareUpdate
      },
      
      primaryUpdateButtonLabel() {
        var pd = this.device.updatesAvailable['daemon']
        var ps = this.device.updatesAvailable['system']
        var pf = this.device.updatesAvailable['firmware']
        var vo = this.version && this.version.original
        if ((pd && ps) || (pd && pf) || (ps && pf)) {
          return 'Download updates'
        }
        if (pd) {
          return vo ? ('Download daemon update (' + vo + ')') : 'Download daemon update'
        }
        if (ps) {
          return vo ? ('Download system ' + vo) : 'Download system update'
        }
        if (pf) {
          return 'Download firmware update'
        }
        return 'Download update'
      },
      
      rebootButtonLabel() {
        var d = this.device.updatesAvailable['daemon']
        var s = this.device.updatesAvailable['system']
        var f = this.device.updatesAvailable['firmware']
        var selected = [d, s, f].filter(Boolean).length
        if (selected > 1) {
          return 'Reboot to apply updates'
        }
        if (d) {
          return 'Reboot to apply daemon update'
        }
        if (f && !s) {
          return 'Reboot to apply firmware update'
        }
        return 'Reboot to apply system update'
      },
      
      offlineUpdatesSummary() {
        var pd = this.device.updatesAvailable['daemon']
        var ps = this.device.updatesAvailable['system']
        var vo = this.version && this.version.original
        if (pd && ps) {
          return 'Daemon and system updates are available'
        }
        if (vo) {
          return vo + ' is available'
        }
        return 'An update is available'
      },
      
      applyInProgress() {
        return !!this.device.firmwareApplyActive
      },
      
      awaitingReboot() {
        if (this.device.firmwareApplyActive) {
          return false
        }
        if (!this.device.state || !this.device.state.update_state) {
          return false
        }
        var h = this.$helpers
        var us = this.device.state.update_state
        var names = ['daemon', 'system', 'firmware']
        for (var i = 0; i < names.length; i++) {
          var entry = h.findUpdateStateEntry(us, names[i])
          if (entry && h.updateStateStatusNeedsRebootPrompt(entry.status)) {
            return true
          }
        }
        return false
      },

      showDeterminateDownloadProgress() {
        return this.processPercent > 0 && this.isUpdating
      },

      processPercent() {
        if (!this.device.state || !this.device.state.update_state) {
          return 0
        }
        var rows = this.device.state.update_state
        for (var i = 0; i < rows.length; i++) {
          var row = rows[i]
          if (!row || row.progress_percent == null || row.progress_percent === '') {
            continue
          }
          var value = parseFloat(row.progress_percent)
          if (!isNaN(value)) {
            return value
          }
        }
        return 0
      },
      
      disableUpdateButton() {
        if (!this.canEdit || !this.device.isOnline() || this.device.isUpdating()) {
          return true
        }
        return false
      },
      
      isUpdating() {
        return this.device.isUpdating()
      },
      
      updateStatus() {
        return (this.device.command === 'update_device') ? this.device.command_status : false
      },
      
      updateMessage() {
        return (this.device.command === 'update_device') ? this.device.command_message : ''
      },
      
      updateStateDetailRows() {
        var h = this.$helpers
        var us = (this.device.state && this.device.state.update_state) ? this.device.state.update_state : []
        var rows = h.updateStateRowsNotUpToDate(us, this.device.state, this.device.product_name).slice()
        var hasModuleRow = function (moduleName) {
          var m = String(moduleName).toLowerCase()
          return rows.some(function (r) {
            return r && r.module != null && String(r.module).toLowerCase() === m
          })
        }
        var state = this.device.state
        var product = this.device.product_name
        
        var daemon = h.findUpdateStateEntry(us, 'daemon')
        if (daemon && h.updateStateRowPendingForDevice(daemon, state, product) && !hasModuleRow('daemon')) {
          rows.unshift(daemon)
        }
        
        var system = h.findUpdateStateEntry(us, 'system')
        if (system && h.updateStateRowPendingForDevice(system, state, product) && !hasModuleRow('system')) {
          rows.unshift(system)
        }

        var forced = this.device.forcedDownloadModules || []
        for (var f = 0; f < forced.length; f++) {
          var name = String(forced[f]).toLowerCase()
          if (!this.device.updatesAvailable || !this.device.updatesAvailable[name] || hasModuleRow(name)) {
            continue
          }
          var forcedRow = h.findUpdateStateEntry(us, name)
          rows.push(forcedRow || {
            module: name,
            status: 'Update available for download',
            target_version: this.device.updatesAvailable[name].original || ''
          })
        }
        
        return rows
      },
    },
    methods: {
      rebootDevice() {
        if (this.rebootDeviceConfirm == 'CONFIRM') {
          this.showRebootDevice = false
          this.updateAction(firmwareUpdateContract.dedicatedRebootToUpdateCommand(
            this.device.product_name,
            this.device.state
          ))
          this.$emit('close')
        }
      },
      
      updateAction(command = 'download_update') {
        runDeviceFirmwareCommand(this, command).catch(function () {
          /* error state set on device */
        })
      }
    }
  }
</script>